/**
 * AP-32 Nachtrag N-4 — **Das Foto einer Belegzeile in der Maske.**
 *
 * Geladen mit dem Anmelde-Token über `GET /documents/:id/lines/:lineId/image`
 * und als Objekt-URL gezeigt. Eine vorsignierte Speicher-Adresse trüge den
 * Hostnamen, unter dem das Backend den Speicher erreicht (`minio:9000`) — der
 * Browser kennt ihn nicht, das Bild bliebe leer.
 *
 * Der Cache hängt an Zeile **und** Fotostand: Ein ersetztes Foto hat einen neuen
 * Stand und wird neu geladen, ein unverändertes nicht bei jedem Speichern.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { documentsApi } from '../api/documents.api'

const cache = new Map<string, string>()

interface Props {
  documentId: string
  lineId: string
  version: string | null
  className?: string
}

export function LineImage({ documentId, lineId, version, className }: Props) {
  const { t } = useTranslation()
  const schluessel = `${lineId}:${version ?? ''}`
  const [url, setUrl] = useState<string | null>(() => cache.get(schluessel) ?? null)
  const [fehler, setFehler] = useState(false)

  useEffect(() => {
    const vorhanden = cache.get(schluessel)
    if (vorhanden) {
      setUrl(vorhanden)
      return
    }
    let aktiv = true
    setFehler(false)
    documentsApi
      .lineImage(documentId, lineId)
      .then(({ data }) => {
        const neu = URL.createObjectURL(data)
        cache.set(schluessel, neu)
        if (aktiv) setUrl(neu)
      })
      .catch(() => {
        if (aktiv) setFehler(true)
      })
    return () => {
      aktiv = false
    }
  }, [documentId, lineId, schluessel])

  if (fehler) return <span className="mt-1 block text-xs text-destructive">{t('documents.grid.photoLoadFailed')}</span>
  if (!url) return <span className="mt-1 block h-16 w-24 animate-pulse rounded border border-border bg-muted" />
  return <img src={url} alt="" className={className} />
}

/** N-104: ein Foto, das an der Zeile auf „Speichern" wartet — gezeigt aus der Datei im Browser. */
export function VorlaeufigesFoto({ datei, className }: { datei: File; className?: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    const u = URL.createObjectURL(datei)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [datei])
  return url ? <img src={url} alt={datei.name} className={className} /> : null
}
